/**
 * What the client sees when the portal breaks.
 *
 * The DC runtime caught render errors itself and painted a red box with the message in it
 * (support.js:858-867). Nothing here does that: a stack trace is not something to show a dog
 * owner, and `#b00020` is not in the Organic palette. This is the designed replacement.
 *
 * TWO LIMITS WORTH KNOWING BEFORE TRUSTING THIS. An error boundary catches errors thrown while
 * REACT IS RENDERING. It does not catch a throw inside an event handler, inside a `setTimeout`, or
 * inside a promise — those reach `window.onerror` and this component never hears about them. And
 * it cannot catch an error thrown by an ancestor, which is why `<PortalProvider>` sits outside it
 * in main.tsx and is deliberately kept free of anything that can throw at render time.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

import { PHONE_DISPLAY, PHONE_HREF } from '../model/contact'
import { Icon, UI_ICON_PATHS } from '../model/icons'

export interface ErrorBoundaryProps {
  readonly children?: ReactNode
}

interface ErrorBoundaryState {
  readonly failed: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    // The Error object itself is deliberately NOT kept in state. Holding it invites someone to
    // render `error.message` "just for support", and a React render error's message routinely
    // quotes the props that produced it — which here means a client's own record.
    this.state = { failed: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only sink. There is no error reporter wired up, and adding one silently
    // would ship a client's record off-origin — the whole API contract exists to stop that.
    console.error('[portal] render failed', error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children

    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '26px 18px calc(26px + env(safe-area-inset-bottom))',
        }}
      >
        <section
          className="card elev-sm"
          role="alert"
          style={{ width: 'min(430px, 100%)', alignItems: 'center', textAlign: 'center', gap: 0, padding: '36px 26px' }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: 'var(--color-neutral-200)',
              color: 'var(--color-neutral-700)',
              marginBottom: '14px',
            }}
          >
            <Icon paths={UI_ICON_PATHS.close} size={24} />
          </span>
          <h1 style={{ margin: '0 0 6px', fontSize: '26px' }}>Something went wrong</h1>
          <p className="text-muted" style={{ maxWidth: '40ch', margin: '0 0 20px', fontSize: '14px' }}>
            The portal stopped part way through drawing this page. Nothing on your dog’s record has
            been changed. Reloading usually fixes it.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => window.location.reload()}
            style={{ minHeight: '44px' }}
          >
            Reload the page
          </button>
          <p className="text-muted" style={{ margin: '18px 0 0', fontSize: '12.5px', lineHeight: 1.55 }}>
            If it keeps happening, ring us on{' '}
            <a href={PHONE_HREF} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              {PHONE_DISPLAY}
            </a>{' '}
            — we can read your dog’s record out to you.
          </p>
        </section>
      </main>
    )
  }
}
